import type { ReactNode } from 'react';
import { Fragment, useEffect, useState } from 'react';

import { BodyMicro } from '#/components/Text/BodyMicro';
import { H4 } from '#/components/Text/H4';
import { useI18n } from '#/i18n/hooks/useI18n';
import { normalizeDataToPixel } from '#/modules/salary/helpers/normalizeDataToPixel';

import {
  CustomTooltip,
  HoverSection,
  HoverSectionContainer,
  HoverSectionTooltip,
  MainLine,
  StyledRect,
  StyledSvg,
  TooltipContainer,
} from './Line.styled';

const TOOLTIP_HEIGHT = 83;
const TOOLTIP_WIDTH = 107;
const TOOLTIP_TRIANGLE_HEIGHT = 9;

type LineProps = {
  active: boolean;
  chartHeight?: number;
  chartWidth: number;
  className?: string;
  data: [x: number, y: number][];
  fillColor?: string;
  lineWidth?: number;
  initialized: boolean;
  hoverSections: Record<
    string,
    {
      coords: string;
      tooltip?: ReactNode;
    }
  >;
  hoverSectionColor?: string;
  loading: boolean;
  maxX: number;
  maxY: number;
  minX?: number;
  minY?: number;
  offsetX?: number;
  offsetY?: number;
  overrideZIndex?: number;
  qaId?: string;
  showTooltips?: boolean;
  tooltip?: ReactNode;
  tooltipPositionX?: number;
  tooltipPositionY?: number;
  strokeColor?: string;
};

export function Line({
  active = true,
  chartHeight = 240,
  chartWidth = 120,
  lineWidth,
  className,
  data,
  fillColor,
  hoverSections,
  hoverSectionColor,
  initialized,
  loading,
  overrideZIndex,
  maxX,
  maxY,
  minX = 0,
  minY = 0,
  offsetX = 0,
  offsetY = 0,
  qaId,
  showTooltips = false,
  tooltipPositionX,
  tooltipPositionY,
  strokeColor,
  tooltip,
}: LineProps) {
  const { i18n, locale } = useI18n();

  const quadraticBezierCurves: string[] = [];
  const [activeSection, setActiveSection] = useState<string | null>(
    'interval68',
  );

  useEffect(() => {
    if (!active) {
      setActiveSection('interval68');
    }
  }, [active]);

  const shiftedPoints: [x: number, y: number][] = [];

  let moveToX = 0;
  let moveToY = 0;
  let lastX = 0;

  data.forEach(([x, y], i) => {
    if (initialized && showTooltips) {
      shiftedPoints.push([
        (x - minX) / (maxX - minX),
        (y - minY) / (maxY - minY),
      ]);
    }

    if (i === data.length - 1) return;

    const p1 = [x, y];
    const p2 = [data[i + 1][0], data[i + 1][1]];

    const x1 = normalizeDataToPixel(p1[0], minX, maxX, lineWidth ?? chartWidth);
    const y1 = normalizeDataToPixel(p1[1], maxY, minY, chartHeight);
    const endX = normalizeDataToPixel(
      p2[0],
      minX,
      maxX,
      lineWidth ?? chartWidth,
    );
    const endY = normalizeDataToPixel(p2[1], maxY, minY, chartHeight);

    if (i === 0) {
      moveToX = x1;
      moveToY = y1;
      return;
    }

    if (i === data.length - 2) {
      lastX = endX;
    }

    quadraticBezierCurves.push(`Q ${x1} ${y1}, ${endX} ${endY}`);
  });

  const d = `M ${moveToX} ${chartHeight} L ${moveToX} ${moveToY} ${quadraticBezierCurves.join(
    '',
  )} L ${lastX} ${chartHeight}`;

  return (
    <StyledSvg
      className={className}
      $active={active}
      $height={chartHeight}
      $initialized={initialized}
      $loading={loading}
      $overrideZIndex={overrideZIndex}
      data-qa-id={qaId}
      viewBox={`${offsetX} ${offsetY} ${chartWidth} ${chartHeight}`}
      height={chartHeight}
      width={chartWidth}
      xmlns="http://www.w3.org/2000/svg"
    >
      <MainLine
        key={d}
        d={d}
        $active={active}
        $initialized={initialized}
        $loading={loading}
        $fill={fillColor}
        $hoverFill={hoverSectionColor}
        $stroke={strokeColor}
      />
      {active && hoverSections ? (
        <HoverSectionContainer>
          {Object.keys(hoverSections).map((section) => (
            <Fragment key={section}>
              {hoverSections[section].tooltip ? (
                <foreignObject
                  x="0"
                  y="-60"
                  width={chartWidth}
                  height={TOOLTIP_HEIGHT}
                >
                  <HoverSectionTooltip $active={activeSection === section}>
                    {hoverSections[section].tooltip}
                  </HoverSectionTooltip>
                </foreignObject>
              ) : null}
              <clipPath id={section}>
                <polygon
                  points={hoverSections[section].coords}
                  fillOpacity={0}
                />
              </clipPath>
              <HoverSection
                $hoverFill={hoverSectionColor}
                $active={activeSection === section}
                key={d}
                d={d}
                clipPath={`url(#${section})`}
                onMouseEnter={() => setActiveSection(section)}
                onMouseLeave={() => setActiveSection(null)}
              />
            </Fragment>
          ))}
        </HoverSectionContainer>
      ) : null}
      {tooltip && chartWidth ? (
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          preserveAspectRatio="none"
        >
          <foreignObject
            x={tooltipPositionX ?? 0}
            y={tooltipPositionY ?? 0}
            width="320"
            height={TOOLTIP_HEIGHT}
          >
            <CustomTooltip $direction="up">{tooltip}</CustomTooltip>
          </foreignObject>
        </svg>
      ) : null}
      {initialized && showTooltips
        ? shiftedPoints.map(([x, y], i) => {
            const tooltipDirection =
              x * (lineWidth ?? chartWidth) + 20 + TOOLTIP_WIDTH + 84 >
              window.innerWidth
                ? 'left'
                : 'right';

            const tooltipX =
              tooltipDirection === 'right'
                ? x * (lineWidth ?? chartWidth) + 20
                : x * (lineWidth ?? chartWidth) - TOOLTIP_WIDTH - 20;

            return (
              <Fragment key={`${x}-${y}`}>
                <StyledRect
                  x={x * (lineWidth ?? chartWidth) - 4}
                  y={chartHeight - y * chartHeight - 4}
                  width="8"
                  height="8"
                  rx="9999"
                  ry="9999"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
                {chartWidth ? (
                  <svg
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    preserveAspectRatio="none"
                  >
                    <foreignObject
                      x={tooltipX}
                      y={
                        chartHeight -
                        y * chartHeight -
                        TOOLTIP_HEIGHT / 2 -
                        TOOLTIP_TRIANGLE_HEIGHT +
                        15
                      }
                      width={TOOLTIP_WIDTH}
                      height={TOOLTIP_HEIGHT}
                    >
                      <CustomTooltip $direction={tooltipDirection}>
                        <TooltipContainer>
                          <BodyMicro lineHeight={1.2} opacity={0.8}>
                            {i18n('Salary')}
                          </BodyMicro>
                          <H4 lineHeight={1.2} my={0}>
                            {Math.round(data[i][0]).toLocaleString(locale, {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                            })}
                          </H4>
                        </TooltipContainer>
                      </CustomTooltip>
                    </foreignObject>
                  </svg>
                ) : null}
              </Fragment>
            );
          })
        : null}
    </StyledSvg>
  );
}
